import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  Image,
  Dimensions,
  Alert,
} from 'react-native';
import { UserService } from '../../services/ApiService';
import Video from 'react-native-video';
// import { getVideoSource } from '../../assets/videos/video-config';

const { width, height } = Dimensions.get('window');

const PlanGenerationScreen = ({ jobId, planType, onComplete, onError }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState(null);
  const [loadingText, setLoadingText] = useState('Preparing your personalized plan...');
  const [posterReady, setPosterReady] = useState(true);
  const [videoReady, setVideoReady] = useState(false);

  const spinValue = useRef(new Animated.Value(0)).current;
  const pulseValue = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    // Start animations
    startAnimations();

    // Start polling for job status
    if (jobId) {
      pollJobStatus();
    }
  }, [jobId]);

  const startAnimations = () => {
    // Spinning animation
    Animated.loop(
      Animated.timing(spinValue, {
        toValue: 1,
        duration: 2000,
        useNativeDriver: true,
      })
    ).start();

    // Pulsing animation
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseValue, {
          toValue: 1.1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    ).start();
  };

  const pollJobStatus = async () => {
    const pollInterval = 5000; // 5 seconds
    let pollCount = 0;
    const maxPolls = 60; // Maximum 5 minutes (60 * 5 seconds)

    const poll = async () => {
      try {
        pollCount++;
        console.log(`Polling job status... (${pollCount}/${maxPolls})`);

        const response = await UserService.getJobStatus(jobId);
        console.log('Job status response:', response);

        if (!response.success) {
          console.error('Job status request failed:', response.message);
          if (pollCount < maxPolls) {
            setTimeout(poll, pollInterval);
          } else {
            setError('Unable to check plan generation status');
            setTimeout(() => {
              onError && onError(response.message);
            }, 1000);
          }
          return;
        }

        const job = response.job;
        console.log('Job data:', job);

        if (job) {
          setProgress(job.progress || 0);
          setStatus(job.status);

          // Update loading text based on progress
          updateLoadingText(job.progress, job.status);

          if (job.status === 'completed') {
            console.log('Plan generation completed successfully!');
            console.log('Job result:', job.result);
            setTimeout(() => {
              console.log('Calling onComplete callback...');
              onComplete && onComplete(job.result);
            }, 1000);
            return;
          } else if (job.status === 'failed') {
            console.error('Plan generation failed:', job.error);
            setError(job.error || 'Plan generation failed');
            setTimeout(() => {
              onError && onError(job.error);
            }, 1000);
            return;
          }
        }

        // Continue polling if not completed/failed and within limit
        if (pollCount < maxPolls && job.status !== 'completed' && job.status !== 'failed') {
          setTimeout(poll, pollInterval);
        } else if (pollCount >= maxPolls) {
          console.error('Plan generation timeout');
          setError('Plan generation is taking longer than expected. Please try again.');
          setTimeout(() => {
            onError && onError('Timeout');
          }, 1000);
        }

      } catch (error) {
        console.error('Error polling job status:', error);
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        } else {
          setError('Unable to check plan generation status');
          setTimeout(() => {
            onError && onError(error);
          }, 1000);
        }
      }
    };

    poll();
  };

  const updateLoadingText = (progress, status) => {
    if (status === 'pending') {
      setLoadingText('Queuing your plan generation...');
    } else if (status === 'processing') {
      if (progress < 30) {
        setLoadingText('Analyzing your fitness goals...');
      } else if (progress < 60) {
        setLoadingText('Creating your workout plan...');
      } else if (progress < 90) {
        setLoadingText('Designing your nutrition plan...');
      } else {
        setLoadingText('Finalizing your personalized plan...');
      }
    } else if (status === 'completed') {
      setLoadingText('Your plan is ready!');
    } else if (status === 'failed') {
      setLoadingText('Plan generation failed');
    }
  };

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  const GOOGLE_DRIVE_LOADING_FILE_ID = '11sPgqDr-fpb-nLlA0vbmMU1JtKsHcbLw';

  const getGoogleDriveThumbnailUrl = (fileId) => {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
  };

  // const getGoogleDriveDirectVideoUrl = (fileId) => {
  //   return `https://drive.google.com/uc?export=download&id=${fileId}`;
  // };

  const posterUri = getGoogleDriveThumbnailUrl(GOOGLE_DRIVE_LOADING_FILE_ID);
  // const videoUri = getGoogleDriveDirectVideoUrl(GOOGLE_DRIVE_LOADING_FILE_ID);

  const getPlanTypeDisplay = () => {
    switch (planType) {
      case 'basic':
        return 'Basic AI Plan';
      case 'pro':
        return 'Pro AI Plan';
      case 'human':
        return 'Elite Human Trainer Plan';
      default:
        return 'AI Plan';
    }
  };

  useEffect(() => {
    const uri = posterUri;
    let cancelled = false;

    (async () => {
      try {
        await Image.prefetch(uri);
      } catch (e) {
        // Non-fatal: we'll still render the poster normally.
      } finally {
        if (!cancelled) setPosterReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Plan Type Header */}
        <Text style={styles.planTypeText}>{getPlanTypeDisplay()}</Text>

        {/* Video or Animated Image */}
        <Animated.View
          style={[
            styles.mediaContainer,
            {
              transform: [
                { scale: pulseValue },
                { rotate: spin }
              ]
            }
          ]}
        >
          <View style={styles.mediaInner}>
            <Image
              source={{ uri: posterUri }}
              style={[styles.planImage, !posterReady && { opacity: 0 }]}
              resizeMode="cover"
            />
            <Video
              source={{ uri: videoUri }}
              style={[styles.planVideo, !videoReady && { opacity: 0 }]}
              resizeMode="cover"
              repeat
              muted
              paused={false}
              onLoad={() => setVideoReady(true)}
              onError={() => setVideoReady(true)}
            />
          </View>
        </Animated.View>



        {/* Loading Text */}
        <Text style={styles.loadingText}>{loadingText}</Text>

        {/* Time Notice */}
        <View style={styles.timeNoticeContainer}>
          <Text style={styles.timeNoticeText}>
            Plan generation may take a few minutes. You can wait here or come back later to check your progress.
          </Text>
        </View>

        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View
              style={[
                styles.progressFill,
                { width: `${progress}%` }
              ]}
            />
          </View>
          <Text style={styles.progressText}>{progress}%</Text>
        </View>

        {/* Status */}
        <Text style={styles.statusText}>
          {status === 'pending' && 'Queued'}
          {status === 'processing' && 'Generating'}
          {status === 'completed' && 'Complete'}
          {status === 'failed' && 'Failed'}
        </Text>

        {/* Error Message */}
        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {/* Tips */}
        <View style={styles.tipsContainer}>
          <Text style={styles.tipsTitle}>Did you know?</Text>
          <Text style={styles.tipsText}>
            Our AI analyzes your goals, fitness level, and preferences to create a personalized plan just for you!
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    alignItems: 'center',
    maxWidth: width * 0.9,
  },
  planTypeText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 30,
    textAlign: 'center',
  },
  mediaContainer: {
    width: 200,
    height: 200,
    borderRadius: 100,
    marginBottom: 30,
    backgroundColor: '#161C2D',
    position: 'relative',
    shadowcolor: '#FFFFFF',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    overflow: 'hidden',
  },
  planVideo: {
    width: '100%',
    height: '100%',
    borderRadius: 100,
    position: 'absolute',
    top: 0,
    left: 0,
  },
  planImage: {
    width: '100%',
    height: '100%',
    borderRadius: 100,
    position: 'absolute',
    top: 0,
    left: 0,
  },
  mediaInner: {
    width: '100%',
    height: '100%',
  },
  loadingText: {
    fontSize: 18,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 20,
    fontWeight: '500',
  },
  timeNoticeContainer: {
    backgroundColor: '#0B0E14',
    borderColor: '#FACC15',
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    width: '100%',
  },
  timeNoticeText: {
    fontSize: 14,
    color: '#92400e',
    textAlign: 'center',
    lineHeight: 20,
    fontWeight: '500',
  },
  progressContainer: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 20,
  },
  progressBar: {
    width: '100%',
    height: 8,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 10,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#3b82f6',
  },
  statusText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6D759E',
    marginBottom: 20,
  },
  errorContainer: {
    backgroundColor: '#0B0E14',
    borderColor: '#fecaca',
    borderWidth: 1,
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    width: '100%',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
    textAlign: 'center',
  },
  tipsContainer: {
    backgroundColor: '#0B0E14',
    borderColor: '#bae6fd',
    borderWidth: 1,
    borderRadius: 12,
    padding: 20,
    width: '100%',
    marginTop: 20,
  },
  tipsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#3B82F6',
    marginBottom: 8,
    textAlign: 'center',
  },
  tipsText: {
    fontSize: 14,
    color: '#0c4a6e',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default PlanGenerationScreen;
